import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TagCategoryEnum } from 'state/constants'
import {
    closeDeleteTagModal,
    closeProfileTagModal,
    deletePartiposterTagRequest,
    savePartiposterTagRequest,
    selectDeleteTagModal,
    selectProfileTagModal,
    updatePartiposterTagRequest,
} from 'state/partiposter'
import { STATE_KEYS } from 'state/requests'
import { selectRequestsAreLoading } from 'state'
import { TAG_LABELS } from '.'
import styled from 'particles/themes/styled'
import Modal from 'particles/Modal'
import RoundTag, { getSeverityState } from 'particles/Tag'
import UserProfile from 'components/Tag/UserProfile'
import TextArea from 'particles/TextInputs/TextArea'
import Text from 'particles/Text'
import Divider from 'components/Shared/Divider'

export const getProfileTagIconType = (categoryEnum: TagCategoryEnum) => {
    switch (categoryEnum) {
        case TagCategoryEnum.CAPTION_QUALITY:
            return 'listBullets'
        case TagCategoryEnum.INFLUENCER_TYPE:
            return 'user'
        case TagCategoryEnum.PHOTO_QUALITY:
            return 'image'
        default:
            return undefined
    }
}
const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[400]};
    .note-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }
`

const ProfileTagModal: React.FC = () => {
    const [t] = useTranslation()
    const [note, setNote] = useState({ value: '', error: '' })
    const {
        isOpen,
        categoryEnum,
        selectedTag,
        selectedProfile,
        submissionUuid,
        uuid: profileToTagUuid,
    } = useSelector(selectProfileTagModal)
    const dispatch = useDispatch()

    const onCloseModal = useCallback(() => dispatch(closeProfileTagModal()), [])
    const onSubmit = () => {
        if (selectedTag?.uuid && submissionUuid && selectedProfile?.uuid) {
            dispatch(
                profileToTagUuid
                    ? updatePartiposterTagRequest({
                          tag_uuid: selectedTag.uuid,
                          text: note.value,
                          uuid: profileToTagUuid,
                          submissionUuid,
                          profileUuid: selectedProfile.uuid,
                      })
                    : savePartiposterTagRequest({
                          tags: [
                              {
                                  uuid: selectedTag.uuid,
                                  text: note.value,
                              },
                          ],
                          submissionUuid,
                          profileUuid: selectedProfile.uuid,
                          isProfileTag: true,
                      }),
            )
        }
    }

    if (!categoryEnum || !selectedTag || !selectedProfile) {
        return <div />
    }
    if (!isOpen) return null
    return (
        <Modal
            onClose={onCloseModal}
            size="m"
            title={t('modalWindow.confirmationMessage.changePartiposterTagTitle', {
                defaultValue: 'Change {{category_enum}} tag',
                category_enum: TAG_LABELS[categoryEnum],
            }).toUpperCase()}
            hasIcon={false}
            primaryButtonProps={{
                label: t('button.confirm'),
            }}
            secondaryButtonProps={{
                label: t('button.cancel'),
            }}
            onConfirm={onSubmit}
        >
            <ModalContainer>
                <UserProfile profileData={selectedProfile} className="profile" />

                <Text scale="bodyM400" color="secondary">
                    {t('modalWindow.changePartiposterTagDescription', {
                        category_enum: TAG_LABELS[categoryEnum].toLowerCase(),
                    })}
                </Text>
                <RoundTag
                    icon={getProfileTagIconType(categoryEnum)}
                    contentType={getSeverityState(selectedTag.severity)}
                    label={TAG_LABELS[selectedTag.name]}
                />
                <Divider />
                <div className="note-container">
                    <Text scale="bodyM500">{t('submission.tagHistroy.addNotes')}</Text>
                    <TextArea
                        minTextAreaHeight={80}
                        value={note.value}
                        maxInputLength={1000}
                        showInputLength
                        onChange={value => {
                            setNote({
                                ...note,
                                value: value,
                                error: value.length > 1000 ? t('errorMessage.tagNotes.overWordCount') : '',
                            })
                        }}
                        error={note.error}
                    />
                </div>
            </ModalContainer>
        </Modal>
    )
}

const DeleteTagModal: React.FC = () => {
    const [t] = useTranslation()
    const {
        selectedTag,
        selectedProfile,
        uuid: profileToTagUuid,
        submissionUuid,
        isOpen,
        nextPartiposterTag,
    } = useSelector(selectDeleteTagModal)
    const dispatch = useDispatch()
    const isDeleting = useSelector(selectRequestsAreLoading([STATE_KEYS.DeletePartiposterTag]))

    const onCloseModal = useCallback(() => dispatch(closeDeleteTagModal()), [])

    const onDeleteTag = useCallback(() => {
        if (selectedProfile) {
            dispatch(
                deletePartiposterTagRequest({
                    submissionUuid,
                    profileUuid: selectedProfile.uuid,
                    uuid: profileToTagUuid,
                    nextPartiposterTag,
                }),
            )
        }
    }, [submissionUuid, selectedProfile])

    if (!selectedTag) {
        return <div />
    }

    return (
        <>
            {isOpen ? (
                <Modal
                    title={t('modalWindow.submission.removeTagTitle', {
                        name: selectedTag.is_custom ? selectedTag.name : TAG_LABELS[selectedTag.name],
                    })}
                    subtext={t('modalWindow.submission.removeTagSubText')}
                    size="m"
                    hasIcon={false}
                    primaryButtonProps={{
                        label: t('button.yesRemove'),
                        isDisabled: isDeleting,
                    }}
                    secondaryButtonProps={{
                        label: t('button.cancel'),
                        isDisabled: isDeleting,
                    }}
                    onConfirm={onDeleteTag}
                    onClose={onCloseModal}
                />
            ) : null}
        </>
    )
}

export { ProfileTagModal, DeleteTagModal }
